import { useQuery } from "@tanstack/react-query";
import {
  ListCardsNoPermissionResponse,
  ListCardsResponseWithProjectName,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { ForbiddenError } from "../errors/forbiddenError.ts";
import { NonRetryableError } from "../errors/nonRetryableError.ts";
import { queryKeys } from "./queryKeys.ts";

const ONE_HOUR = 60 * 60 * 1000;

export const useCards = (projectId: string) => {
  const { getToken, isAuthenticated } = useAuthentication();

  return useQuery({
    queryKey: queryKeys.cardsInProject(projectId),
    queryFn: async ({ signal }): Promise<ListCardsResponseWithProjectName> => {
      const token = await getToken();
      const response = await fetch(`/api/projects/${projectId}/cards`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      if (response.ok) {
        return (await response.json()) as ListCardsResponseWithProjectName;
      }

      if (response.status >= 500 || response.status === 429) {
        throw new Error(`Loading the cards failed: ${response.status}`);
      }

      if (response.status === 403) {
        const responseBody =
          (await response.json()) as ListCardsNoPermissionResponse;

        if (responseBody) {
          const projectInfoWithShepherds: ListCardsResponseWithProjectName = {
            projectDisplayName: responseBody.projectDisplayName,
            projectShepherds: responseBody.projectShepherds,
            projectInfoBox: undefined,
            hiddenFields: [],
            items: [],
            hasPermissions: false,
          };
          return projectInfoWithShepherds;
        } else {
          throw new ForbiddenError("No permission to view the project");
        }
      }

      throw new NonRetryableError(
        `Loading the cards failed: ${response.status}`,
      );
    },
    enabled: isAuthenticated,
    retry: (failureCount, error) =>
      !(error instanceof NonRetryableError) && failureCount < 3,
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });
};
